import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { BrandLogo } from "@/components/BrandLogo";
import { CLAIM_TOKEN_STORAGE_KEY } from "@/routes/ClaimPage";
import { consumeNextPath } from "@/lib/nextPath";

export function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const claimToken = sessionStorage.getItem(CLAIM_TOKEN_STORAGE_KEY);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error: signUpError } = await authClient.signUp.email({ name, email, password });
    setLoading(false);
    if (signUpError) {
      setError(authErrorMessage(signUpError, "Could not create your account."));
      return;
    }
    if (claimToken) {
      navigate({ to: "/onboarding" });
    } else {
      navigate({ to: consumeNextPath() ?? "/" });
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <BrandLogo className="auth-logo" size={22} />
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Set up your Rivets login.</p>
        {claimToken ? <div className="alert alert-success" role="status"><strong>License ready</strong><span>Your purchase will be claimed after account creation.</span></div> : null}

        <form className="panel" onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="name">
              Name
            </label>
            <input
              id="name"
              className="input"
              required
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="hint">Use at least 8 characters.</div>
          </div>
          <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
            {loading ? "Creating account…" : "Create account"}
          </button>
          {error && <div className="error-text">{error}</div>}
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
